import { useState } from 'react';
import { Plus } from 'lucide-react';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useForumPublish } from '@/hooks/useForum';
import { genId } from '@/lib/forum';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export function NewTopicDialog({ forumId }: { forumId: string }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);
  const { user } = useCurrentUser();
  const { publishNode } = useForumPublish();
  const { toast } = useToast();

  if (!user) return null;

  async function submit() {
    if (!title.trim()) {
      toast({ title: 'A topic title is required' });
      return;
    }
    if (!content.trim()) {
      toast({ title: 'Please write the first post' });
      return;
    }
    setBusy(true);
    try {
      await publishNode({
        id: genId('thread'),
        parent: forumId,
        title: title.trim(),
        content: content.trim(),
        type: 'thread',
      });
      toast({ title: 'Topic posted' });
      setOpen(false);
      setTitle('');
      setContent('');
    } catch (e) {
      toast({ title: 'Could not post topic', description: String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#105B86] text-white hover:bg-[#0d4f74]">
          <Plus className="h-4 w-4" />
          New topic
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Post a new topic</DialogTitle>
          <DialogDescription>The first message becomes the topic's opening post.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="topic-title">Title</Label>
            <Input
              id="topic-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="A descriptive topic title"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="topic-content">Message</Label>
            <Textarea
              id="topic-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your opening post…"
              className="min-h-32"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            className="bg-[#105B86] text-white hover:bg-[#0d4f74]"
            onClick={submit}
            disabled={busy}
          >
            {busy ? 'Posting…' : 'Post topic'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
